/**
 * A tela de abertura da Escaramuça.
 *
 * Antes, abrir a aba já era começar a jogar: o tabuleiro subia, a semente era
 * sorteada e a primeira fase começava sem que ninguém tivesse dito que queria
 * jogar. Quem só tinha entrado para ver o mapa perdia a partida que estava em
 * curso, e quem voltava no dia seguinte não tinha como retomar o que deixou
 * pela metade — a partida nem era guardada.
 *
 * Esta tela é o antes: escolher o mapa, retomar o que ficou salvo, e ajustar
 * o que vale para a partida inteira antes de a primeira peça se mover.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import type { AiDifficulty } from '@oz/rules';
import {
  AUTO_SLOT, deleteSave, listSaves, saveLabel, saveWhen,
  type SaveGame, type SlotId,
} from '../game/saves.js';
import { useGamepad, type ConfirmButton } from '../game/useGamepad.js';
import { useDeviceType, loadTouchControlsPref, saveTouchControlsPref, type TouchControlsPref } from '../game/device.js';
import { VirtualTouchControls } from '../components/VirtualTouchControls.js';

/** O mínimo de um mapa para a tela escolher entre eles. */
export interface TitleMapOption {
  readonly id: number;
  readonly name: string;
  readonly width?: number | undefined;
  readonly height?: number | undefined;
}

type Panel = 'raiz' | 'continuar' | 'opcoes';

const DIFFICULTY_LABEL: Readonly<Record<AiDifficulty, string>> = {
  facil: 'Fácil',
  normal: 'Normal',
  dificil: 'Difícil',
};

/** O que cada dificuldade muda, dito antes de a partida começar. */
const DIFFICULTY_NOTE: Readonly<Record<AiDifficulty, string>> = {
  facil: '3 inimigos · a máquina joga com calma',
  normal: '5 inimigos · a máquina pressiona',
  dificil: '8 inimigos · a máquina cerca e não perdoa',
};

export function TitleScreen({
  maps,
  mapId,
  onChangeMap,
  onNewGame,
  onContinue,
  difficulty,
  onChangeDifficulty,
  playMode,
  onChangePlayMode,
  cpuSpeed,
  onChangeCpuSpeed,
  confirmButton,
  onChangeConfirmButton,
  muted,
  onToggleSound,
  onCursor,
  onConfirm,
}: {
  readonly maps: readonly TitleMapOption[];
  readonly mapId: number;
  readonly onChangeMap: (id: number) => void;
  readonly onNewGame: () => void;
  readonly onContinue: (slot: SlotId, save: SaveGame) => void;
  readonly difficulty: AiDifficulty;
  readonly onChangeDifficulty: (value: AiDifficulty) => void;
  readonly playMode: 'vs_cpu' | 'hotseat';
  readonly onChangePlayMode: (value: 'vs_cpu' | 'hotseat') => void;
  readonly cpuSpeed: 'normal' | 'rapido';
  readonly onChangeCpuSpeed: (value: 'normal' | 'rapido') => void;
  readonly confirmButton: ConfirmButton;
  readonly onChangeConfirmButton: (value: ConfirmButton) => void;
  readonly muted: boolean;
  readonly onToggleSound: () => void;
  readonly onCursor?: (() => void) | undefined;
  readonly onConfirm?: (() => void) | undefined;
}): JSX.Element {
  const [panel, setPanel] = useState<Panel>('raiz');
  /*
   * A lista de gravações é lida uma vez e guardada.
   *
   * Ela vem do `localStorage`, que é síncrono: relê-la a cada quadro faria a
   * tela parsear três partidas inteiras em cada movimento do cursor.
   */
  const device = useDeviceType();
  const [touchPref, setTouchPref] = useState<TouchControlsPref>(() => loadTouchControlsPref());
  const [saves, setSaves] = useState(() => listSaves());
  const refreshSaves = useCallback(() => setSaves(listSaves()), []);

  const [cursor, setCursor] = useState(0);

  const current = useMemo(
    () => maps.find((m) => m.id === mapId) ?? maps[0],
    [maps, mapId],
  );

  const cycleMap = useCallback(
    (delta: number) => {
      if (maps.length <= 1) return;
      const at = maps.findIndex((m) => m.id === mapId);
      const next = maps[((at >= 0 ? at : 0) + delta + maps.length) % maps.length];
      if (next) {
        onCursor?.();
        onChangeMap(next.id);
      }
    },
    [maps, mapId, onChangeMap, onCursor],
  );

  const rootEntries = useMemo<readonly string[]>(
    () => (saves.length > 0 ? ['novo', 'continuar', 'opcoes'] : ['novo', 'opcoes']),
    [saves.length],
  );

  useEffect(() => {
    setCursor(0);
  }, [panel]);

  const activate = useCallback(
    (entry: string) => {
      onConfirm?.();
      if (entry === 'novo') onNewGame();
      else if (entry === 'continuar') setPanel('continuar');
      else if (entry === 'opcoes') setPanel('opcoes');
    },
    [onNewGame, onConfirm],
  );

  /*
   * A navegação da tela mora aqui, e não dentro do ouvinte de teclado.
   *
   * Teclado e controle dizem a MESMA coisa — descer um item, trocar de mapa,
   * confirmar, voltar —, e enquanto isso vivia só no `keydown` a tela de
   * abertura era intransitável no joystick: quem jogava de controle tinha de
   * largá-lo, pegar o teclado para começar a partida e só então voltar. Com os
   * verbos separados, cada aparelho é só mais uma boca para os mesmos verbos.
   */
  const moveCursor = useCallback(
    (delta: number) => {
      const size = panel === 'raiz' ? rootEntries.length : 0;
      if (size <= 0) return;
      onCursor?.();
      setCursor((c) => (c + delta + size) % size);
    },
    [panel, rootEntries.length, onCursor],
  );

  const goBack = useCallback(() => {
    if (panel !== 'raiz') setPanel('raiz');
  }, [panel]);

  const confirmEntry = useCallback(() => {
    if (panel !== 'raiz') return;
    const entry = rootEntries[cursor];
    if (entry) activate(entry);
  }, [panel, rootEntries, cursor, activate]);

  // O teclado governa a tela toda: ↑ ↓ escolhem, ← → trocam o mapa, Enter
  // confirma e Esc volta. É o vocabulário que a partida usa depois.
  useEffect(() => {
    const onKey = (event: KeyboardEvent): void => {
      const keys = ['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight', 'Enter', ' ', 'Escape'];
      if (!keys.includes(event.key)) return;
      event.preventDefault();

      if (event.key === 'Escape') goBack();
      else if (event.key === 'ArrowUp') moveCursor(-1);
      else if (event.key === 'ArrowDown') moveCursor(1);
      else if (event.key === 'ArrowLeft' && panel === 'raiz') cycleMap(-1);
      else if (event.key === 'ArrowRight' && panel === 'raiz') cycleMap(1);
      else if (event.key === 'Enter' || event.key === ' ') confirmEntry();
    };

    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [panel, cycleMap, moveCursor, goBack, confirmEntry]);

  // E o controle diz os mesmos verbos. `confirmButton` é a preferência que a
  // própria tela deixa ajustar em Opções, para que trocar o botão valha já na
  // tela em que se trocou.
  const gamepad = useGamepad(
    {
      onDirection: (dx, dy) => {
        if (dy !== 0) moveCursor(dy > 0 ? 1 : -1);
        else if (dx !== 0 && panel === 'raiz') cycleMap(dx > 0 ? 1 : -1);
      },
      onConfirm: confirmEntry,
      onCancel: goBack,
      onToggleMute: onToggleSound,
    },
    true,
    confirmButton,
  );

  return (
    <section data-panel="escaramuça" className="titlescreen">
      <div className="titlescreen-frame">
        <header className="titlescreen-head">
          <p className="titlescreen-eyebrow">Tático por turnos</p>
          <h1 className="titlescreen-logo">OZ EMBLEM</h1>
          <p className="titlescreen-tagline">
            Escolha o campo, monte o esquadrão e comande a batalha.
          </p>
        </header>

        {panel === 'raiz' && (
          <nav className="titlescreen-menu" aria-label="Menu principal">
            <button
              type="button"
              className={`titlescreen-btn ${rootEntries[cursor] === 'novo' ? 'is-active' : ''}`}
              onMouseEnter={() => setCursor(rootEntries.indexOf('novo'))}
              onClick={() => activate('novo')}
            >
              <span className="titlescreen-btn-bullet">▶</span>
              <span className="titlescreen-btn-body">
                <span className="titlescreen-btn-title">Novo jogo</span>
                <span className="titlescreen-btn-desc">
                  Uma escaramuça nova, com elenco sorteado
                </span>
              </span>
            </button>

            {/* O mapa fica DENTRO do menu, e não numa tela à parte: escolher
                onde se vai lutar é metade da decisão de começar. */}
            <div className="titlescreen-mapline">
              <button
                type="button"
                className="titlescreen-mapnav"
                onClick={() => cycleMap(-1)}
                title="Mapa anterior (←)"
                disabled={maps.length <= 1}
              >
                ◀
              </button>
              <span className="titlescreen-mapname">
                🗺 {current ? `#${current.id} · ${current.name}` : 'carregando mapas…'}
                {current?.width && current.height && (
                  <em> ({current.width}×{current.height})</em>
                )}
              </span>
              <button
                type="button"
                className="titlescreen-mapnav"
                onClick={() => cycleMap(1)}
                title="Próximo mapa (→)"
                disabled={maps.length <= 1}
              >
                ▶
              </button>
            </div>

            {saves.length > 0 && (
              <button
                type="button"
                className={`titlescreen-btn ${rootEntries[cursor] === 'continuar' ? 'is-active' : ''}`}
                onMouseEnter={() => setCursor(rootEntries.indexOf('continuar'))}
                onClick={() => activate('continuar')}
              >
                <span className="titlescreen-btn-bullet">▶</span>
                <span className="titlescreen-btn-body">
                  <span className="titlescreen-btn-title">Continuar</span>
                  <span className="titlescreen-btn-desc">
                    {saves.length} partida(s) guardada(s) · a mais recente:{' '}
                    {saveLabel(saves[0]!.save)}
                  </span>
                </span>
              </button>
            )}

            <button
              type="button"
              className={`titlescreen-btn ${rootEntries[cursor] === 'opcoes' ? 'is-active' : ''}`}
              onMouseEnter={() => setCursor(rootEntries.indexOf('opcoes'))}
              onClick={() => activate('opcoes')}
            >
              <span className="titlescreen-btn-bullet">▶</span>
              <span className="titlescreen-btn-body">
                <span className="titlescreen-btn-title">Opções</span>
                <span className="titlescreen-btn-desc">
                  {DIFFICULTY_LABEL[difficulty]} ·{' '}
                  {playMode === 'vs_cpu' ? 'Contra a CPU' : '2 jogadores'} ·{' '}
                  {muted ? 'sem som' : 'com som'}
                </span>
              </span>
            </button>

            <p className="titlescreen-hint">
              ↑ ↓ escolhem · ← → trocam o mapa · Enter confirma
            </p>
          </nav>
        )}

        {panel === 'continuar' && (
          <div className="titlescreen-panel">
            <h2 className="titlescreen-panel-title">Continuar partida</h2>
            {saves.length === 0 && (
              <p className="muted">Nenhuma partida guardada neste navegador.</p>
            )}
            <ul className="savelist">
              {saves.map(({ slot, save }) => (
                <li key={String(slot)} className="savelist-row">
                  <button
                    type="button"
                    className="savelist-load"
                    onClick={() => {
                      onConfirm?.();
                      onContinue(slot, save);
                    }}
                  >
                    <span className="savelist-slot">
                      {slot === AUTO_SLOT ? 'AUTO' : `ESPAÇO ${slot}`}
                    </span>
                    <span className="savelist-body">
                      <span className="savelist-title">
                        {save.mode === 'campanha' ? '📖 ' : '⚔ '}
                        {saveLabel(save)}
                      </span>
                      <span className="savelist-meta">
                        {save.allies} aliado(s) de pé · {save.enemies} inimigo(s) ·{' '}
                        {DIFFICULTY_LABEL[save.difficulty]} · {saveWhen(save)}
                      </span>
                    </span>
                  </button>
                  <button
                    type="button"
                    className="savelist-delete"
                    title="Apagar esta gravação"
                    onClick={() => {
                      if (!confirm('Apagar esta partida guardada?')) return;
                      deleteSave(slot);
                      refreshSaves();
                    }}
                  >
                    🗑
                  </button>
                </li>
              ))}
            </ul>
            <button type="button" className="titlescreen-back" onClick={() => setPanel('raiz')}>
              ← voltar
            </button>
          </div>
        )}

        {panel === 'opcoes' && (
          <div className="titlescreen-panel">
            <h2 className="titlescreen-panel-title">Opções</h2>

            <div className="titlescreen-option">
              <span className="titlescreen-option-label">Dificuldade</span>
              <div className="titlescreen-choices">
                {(['facil', 'normal', 'dificil'] as const).map((value) => (
                  <button
                    key={value}
                    type="button"
                    className={`opt-choice-btn ${difficulty === value ? 'active' : ''}`}
                    onClick={() => onChangeDifficulty(value)}
                  >
                    {DIFFICULTY_LABEL[value]}
                  </button>
                ))}
              </div>
              <span className="titlescreen-option-note">{DIFFICULTY_NOTE[difficulty]}</span>
            </div>

            <div className="titlescreen-option">
              <span className="titlescreen-option-label">Modo</span>
              <div className="titlescreen-choices">
                <button
                  type="button"
                  className={`opt-choice-btn ${playMode === 'vs_cpu' ? 'active' : ''}`}
                  onClick={() => onChangePlayMode('vs_cpu')}
                >
                  Contra a CPU
                </button>
                <button
                  type="button"
                  className={`opt-choice-btn ${playMode === 'hotseat' ? 'active' : ''}`}
                  onClick={() => onChangePlayMode('hotseat')}
                >
                  2 jogadores
                </button>
              </div>
            </div>

            {playMode === 'vs_cpu' && (
              <div className="titlescreen-option">
                <span className="titlescreen-option-label">Ritmo da CPU</span>
                <div className="titlescreen-choices">
                  <button
                    type="button"
                    className={`opt-choice-btn ${cpuSpeed === 'normal' ? 'active' : ''}`}
                    onClick={() => onChangeCpuSpeed('normal')}
                  >
                    Normal
                  </button>
                  <button
                    type="button"
                    className={`opt-choice-btn ${cpuSpeed === 'rapido' ? 'active' : ''}`}
                    onClick={() => onChangeCpuSpeed('rapido')}
                  >
                    Rápido
                  </button>
                </div>
              </div>
            )}

            <div className="titlescreen-option">
              <span className="titlescreen-option-label">Som</span>
              <div className="titlescreen-choices">
                <button type="button" className="opt-choice-btn active" onClick={onToggleSound}>
                  {muted ? '🔇 Mudo' : '🔊 Ligado'}
                </button>
              </div>
            </div>

            <div className="titlescreen-option">
              <span className="titlescreen-option-label">Controles na tela</span>
              <div className="titlescreen-choices">
                {([
                  ['auto', 'Automático (Mobile)'],
                  ['sempre', 'Sempre visível'],
                  ['nunca', 'Desligado'],
                ] as const).map(([value, label]) => (
                  <button
                    key={value}
                    type="button"
                    className={`opt-choice-btn ${touchPref === value ? 'active' : ''}`}
                    onClick={() => {
                      setTouchPref(value);
                      saveTouchControlsPref(value);
                      device.setTouchPref(value);
                    }}
                  >
                    {label}
                  </button>
                ))}
              </div>
              <span className="titlescreen-option-note">
                No celular os botões transparentes aparecem na tela, e somem caso um joystick seja ativado.
              </span>
            </div>

            <div className="titlescreen-option">
              <span className="titlescreen-option-label">Confirmar no controle</span>
              <div className="titlescreen-choices">
                {([
                  ['inferior', 'Botão de baixo'],
                  ['direita', 'Botão da direita'],
                ] as const).map(([value, label]) => (
                  <button
                    key={value}
                    type="button"
                    className={`opt-choice-btn ${confirmButton === value ? 'active' : ''}`}
                    onClick={() => onChangeConfirmButton(value)}
                  >
                    {label}
                  </button>
                ))}
              </div>
              <span className="titlescreen-option-note">
                A no Xbox · ✕ no PlayStation · B no Switch — um adaptador genérico às
                vezes troca os dois, e aqui a escolha é sua.
              </span>
            </div>

            <button type="button" className="titlescreen-back" onClick={() => setPanel('raiz')}>
              ← voltar
            </button>
          </div>
        )}
      </div>

      <VirtualTouchControls
        visible={device.shouldShowTouchControls(gamepad.isConnected)}
        confirmButton={confirmButton}
        onDirection={(dx, dy) => {
          if (dy !== 0) moveCursor(dy > 0 ? 1 : -1);
          else if (dx !== 0 && panel === 'raiz') cycleMap(dx > 0 ? 1 : -1);
        }}
        onConfirm={confirmEntry}
        onCancel={goBack}
      />
    </section>
  );
}
